import { useState, useRef, useEffect } from 'react'
import { Send, Trash2, Sparkles, Loader2, Copy, Check, Plus, RotateCcw } from 'lucide-react'
import { clsx } from 'clsx'
import Markdown from 'react-markdown'
import { useAIChatStore } from '@/stores/aiChatStore'
import { AIConfirmDialog } from './AIConfirmDialog'

function formatTime(isoString: string): string {
  const d = new Date(isoString)
  if (isNaN(d.getTime())) return ''
  const h = d.getHours()
  const m = d.getMinutes().toString().padStart(2, '0')
  const period = h < 12 ? '오전' : '오후'
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${period} ${hour12}:${m}`
}

export function AIChatPage({ embedded = false }: { embedded?: boolean }) {
  const { messages, isLoading, sendMessage, retryMessage, loadHistory, clearHistory, startNewSession, pendingApproval } = useAIChatStore()
  const [text, setText] = useState('')
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [initialized, setInitialized] = useState(false)

  // 초기 로드 (1회만)
  useEffect(() => {
    loadHistory().then(() => setInitialized(true))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 새 메시지 시 스크롤
  useEffect(() => {
    if (initialized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages.length, initialized])

  const handleSend = async () => {
    if (!text.trim() || isLoading) return
    const content = text.trim()
    setText('')
    await sendMessage(content)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleClear = async () => {
    if (messages.length === 0) return
    if (!window.confirm('모든 대화 내용이 삭제됩니다. 계속하시겠습니까?')) return
    await clearHistory()
  }

  const handleCopy = async (content: string, id?: number) => {
    try {
      await navigator.clipboard.writeText(content)
      if (id) { setCopiedId(id); setTimeout(() => setCopiedId(null), 2000) }
    } catch { /* fallback 없음 */ }
  }

  return (
    <div
      className={clsx(
        'flex flex-col',
        embedded
          ? 'h-[calc(100dvh-280px)]'
          : 'workspace-panel mx-auto my-4 h-[calc(100dvh-166px)] w-[calc(100%-2rem)] max-w-5xl overflow-hidden sm:h-[calc(100dvh-120px)] lg:my-7 lg:h-[calc(100dvh-128px)]',
      )}
    >
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-base">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-heading">AI 코치</h2>
            <p className="text-meta text-caption">나만의 생산성 어시스턴트</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={startNewSession}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center text-caption hover:text-violet-500 hover-surface rounded-xl transition-colors"
              title="새 대화"
              aria-label="새 대화"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center text-caption hover:text-red-500 hover-surface rounded-xl transition-colors"
              title="대화 초기화"
              aria-label="대화 초기화"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* 메시지 영역 */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
        {messages.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-100 to-purple-100 dark:from-violet-900/30 dark:to-purple-900/30 flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-violet-500" />
            </div>
            <h3 className="text-base font-semibold text-heading mb-2">AI 코치에게 물어보세요</h3>
            <p className="text-sm text-caption mb-6 max-w-xs">
              오늘 할일, 생산성 분석, 일정 관리 등 무엇이든 도와드려요.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {['오늘 할일 정리해줘', '요즘 생산성 분석해줘', '이번 주 남은 거 알려줘'].map((q) => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="min-h-[44px] px-3 text-label text-caption bg-surface-tertiary hover:bg-[var(--hover-bg)] rounded-xl transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div
            key={msg.id || idx}
            className={clsx('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}
          >
            <div className={clsx('max-w-[85%] flex flex-col', msg.role === 'user' ? 'items-end' : 'items-start')}>
              <div
                className={clsx(
                  'rounded-2xl px-4 py-2.5 text-sm break-words relative group',
                  msg.role === 'user'
                    ? 'bg-primary-500 text-white rounded-br-sm whitespace-pre-wrap'
                    : 'bg-surface-tertiary text-heading rounded-bl-sm'
                )}
              >
                {msg.role === 'assistant' && (
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-violet-500" />
                      <span className="text-label font-medium text-violet-500">AI 코치</span>
                    </div>
                    {/* 복사 버튼 */}
                    <button
                      onClick={() => handleCopy(msg.content, msg.id)}
                      className="p-1 rounded text-caption hover:text-heading opacity-0 group-hover:opacity-100 transition-opacity"
                      title="복사"
                    >
                      {copiedId === msg.id ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                    </button>
                  </div>
                )}
                {/* 마크다운 렌더링 (AI) vs 플레인 텍스트 (유저) */}
                {msg.role === 'assistant' ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none text-sm prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-1.5 prose-headings:text-sm prose-headings:font-semibold prose-pre:my-1 prose-code:text-xs prose-h1:text-sm prose-h2:text-sm prose-h3:text-sm">
                    <Markdown>{msg.content}</Markdown>
                  </div>
                ) : (
                  msg.content
                )}
                {/* 오류 메시지 인라인 재시도 버튼 */}
                {msg.role === 'assistant' && msg.retryPayload !== undefined && (
                  <button
                    onClick={() => retryMessage(msg.createdAt, msg.retryPayload!)}
                    disabled={isLoading}
                    className="mt-2 inline-flex items-center gap-1.5 min-h-[44px] px-3 py-2 rounded-lg text-label font-medium text-caption hover:text-heading bg-surface-secondary hover:bg-surface-hover border border-base transition-colors disabled:opacity-50"
                  >
                    <RotateCcw className="w-4 h-4" />
                    다시 시도
                  </button>
                )}
              </div>
              {/* 타임스탬프 */}
              <span className="text-meta text-disabled mt-0.5 px-1">
                {formatTime(msg.createdAt)}
              </span>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-surface-tertiary rounded-2xl rounded-bl-sm px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                <span className="text-sm text-caption">생각하는 중...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* HITL 확인 다이얼로그 — 하단 sticky (P3-1) */}
      {pendingApproval && <AIConfirmDialog />}

      {/* 입력 영역 */}
      <div className="border-t border-base px-3 py-2">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="AI 코치에게 물어보세요..."
            rows={1}
            className="flex-1 px-3 py-2 rounded-2xl border border-base bg-surface-secondary text-sm text-heading resize-none focus:outline-none focus:ring-1 focus:ring-violet-500 max-h-[120px]"
            style={{ minHeight: '36px' }}
            onInput={(e) => {
              const t = e.target as HTMLTextAreaElement
              t.style.height = '36px'
              t.style.height = `${Math.min(t.scrollHeight, 120)}px`
            }}
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || isLoading}
            className="p-2 bg-violet-500 text-white rounded-full hover:bg-violet-600 disabled:opacity-50 transition-colors flex-shrink-0"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
